import type { ReactNode } from 'react';
import { Alert, Box, Card, Flex, Stack, Text, Title } from '@highlight-cp/core-ui';
import { useTranslation } from 'next-i18next/pages';

import classes from './guided-step-main.module.css';
import { CenteredAbsoluteLoader } from '../../../centered-absolute-loader';

export type GuidedStepMainLayout = 'cardCentered' | 'inline';

export interface GuidedStepMainProps {
  children: ReactNode;
  description?: string;
  /** Optional decorative header illustration path rendered above the title and description. */
  icon?: string;
  isLoading?: boolean;
  isError?: boolean;
  loadingDescription?: string;
  loadingTitle?: string;
  /**
   * `cardCentered` — centered column with max-width card (guided setup).
   * `inline` — full-width main area without card chrome or vertical centering (in-app surfaces).
   */
  layout?: GuidedStepMainLayout;
  title?: string;
}

/**
 * The main content area of a guided step — the fixed footer is {@link GuidedStepFooter}.
 *
 * @param params - {@link GuidedStepMainProps}
 * @param params.icon - Optional header illustration SVG path rendered above the title and description.
 * @param params.layout - `cardCentered` (default): centered max-width card; `inline`: full width, no card, not vertically centered.
 * @returns Main column with card and step copy, or inline full-width when `layout="inline"`.
 */
export const GuidedStepMain = ({
  children,
  description,
  icon,
  isLoading,
  isError,
  loadingDescription,
  loadingTitle,
  layout = 'cardCentered',
  title,
}: GuidedStepMainProps) => {
  const { t } = useTranslation();

  if (isError) {
    return (
      <Alert color="red" title="Sorry, something went wrong!" maw="50%" mx="auto" mt="xl">
        {t('An error occurred while loading this step. Please try again.')}
      </Alert>
    );
  }

  const stepBody = isLoading ? (
    <CenteredAbsoluteLoader
      title={loadingTitle ? t(loadingTitle) : undefined}
      description={loadingDescription ? t(loadingDescription) : undefined}
    />
  ) : (
    <Stack gap="lg">
      {icon || title || description ? (
        <Stack gap="sm">
          {icon ? (
            <Box
              bg="var(--mantine-color-blue-filled)"
              className={classes.iconContainer}
              h={48}
              w={48}
            >
              <img alt="" aria-hidden="true" src={icon} />
            </Box>
          ) : null}
          <Stack gap={4}>
            {title ? <Title order={2}>{t(title)}</Title> : null}
            {description ? <Text>{t(description)}</Text> : null}
          </Stack>
        </Stack>
      ) : null}
      {children}
    </Stack>
  );

  if (layout === 'inline') {
    return (
      <Flex align="flex-start" justify="flex-start" className={classes.flex} w="100%">
        <Stack gap="lg" w="100%">
          {stepBody}
        </Stack>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" className={classes.flex} py="xl" w="100%" mih="100%">
      <Card w="100%" maw={800} miw={420} mih={280}>
        {stepBody}
      </Card>
    </Flex>
  );
};
